import Typography from '@mui/material/Typography';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_CONTAINER_DIALOG } from '../../../../core/constants/dialog';
import { useMeCore } from '../../../../core/modules/me';
import { useContainerCore } from '../../../../core/modules/container';
import { useMasterCore } from '../../../../core/modules/master';
import { useBusinessCore } from '../../../../core/modules/business';
import { useCompanyCore } from '../../../../core/modules/company';
import { useLocationCore } from '../../../../core/modules/location';
import { FilterDrawer, FilterField } from '../../shared/FilterDrawer';

interface ContainerFilters {
  code?: string;
  location_id?: number | 'sin-ubicar';
  type?: number;
  category?: number;
  status?: number;
  recipientType?: number;
  priceRange?: number | 'sin-rango';
}

interface HeaderProps {
  initialSearch?: string;
  initialField?: 'code' | 'name';
}

const Header = ({ initialSearch = '', initialField = 'code' }: HeaderProps) => {
  const { total, setOptions } = useContainerCore();
  const { user } = useMeCore();
  const { getCategories, getTypes, getPriceRanges, containerTypes, containerCategories, priceRanges, containerRecipientTypes } = useMasterCore();
  const { getBusinesses } = useBusinessCore();
  const { companyId } = useCompanyCore();
  const { openDialog } = useGlobalCore();
  const { getLocations, locations } = useLocationCore();

  const [filters, setFilters] = useState<ContainerFilters>({
    code: initialSearch || undefined,
  });

  const canWrite = useMemo(() => user && (user.privileges.superAdmin || user.privileges.writeContainer), [user]);

  // Sincronizar con valores iniciales cuando cambien
  useEffect(() => {
    if (initialSearch) {
      setFilters(prev => ({ ...prev, code: initialSearch }));
    }
  }, [initialSearch]);

  // Cargar datos cuando se monte el componente
  useEffect(() => {
    if (companyId) {
      getLocations(companyId);
      getTypes(companyId, 'container');
      getCategories(companyId, 'container');
      getPriceRanges(companyId);
      getTypes(companyId, 'containerRecipient');
    }
  }, [companyId, getLocations, getTypes, getCategories, getPriceRanges]);

  // Definir estados de contenedor localmente
  const containerStatuses = useMemo(() => [
    { id: 1, name: 'Abierto' },
    { id: 2, name: 'Cerrado' },
    { id: 3, name: 'Pausado' },
    { id: 4, name: 'Disponible' }
  ], []);

  // Aplicar filtros cuando cambien
  useEffect(() => {
    if (companyId) {
      const apiFilters: any = {};
      
      if (filters.code) {
        apiFilters.code = filters.code;
      }
      
      // Filtro de ubicación
      if (filters.location_id !== undefined) {
        if (filters.location_id === 'sin-ubicar') {
          apiFilters.location_id = -1;
        } else {
          apiFilters.location_id = filters.location_id;
        }
      }
      
      // Otros filtros
      if (filters.type) apiFilters.type = filters.type;
      if (filters.category) apiFilters.category = filters.category;
      if (filters.status) apiFilters.status = filters.status;
      if (filters.recipientType) apiFilters.recipientType = filters.recipientType;
      if (filters.priceRange) {
        if (filters.priceRange === 'sin-rango') {
          apiFilters.price_range_id = -1; // Valor especial para "Sin rango"
        } else {
          apiFilters.price_range_id = filters.priceRange;
        }
      }
      
      setOptions(apiFilters);
    }
  }, [companyId, filters, setOptions]);

  const openCreateDialog = useCallback(() => {
    if (companyId){
      getBusinesses(companyId);
      getTypes(companyId, 'container');
      getCategories(companyId, 'container');
      getTypes(companyId, 'channel');
      getPriceRanges(companyId);
    }
    openDialog(CREATE_CONTAINER_DIALOG);
  }, [companyId, getBusinesses, getCategories, getPriceRanges, getTypes, openDialog]);

  const handleFiltersChange = useCallback((newFilters: ContainerFilters) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback((appliedFilters?: ContainerFilters) => {
    if (appliedFilters) {
      setFilters(appliedFilters);
    }
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      code: undefined,
      location_id: undefined,
      type: undefined,
      category: undefined,
      status: undefined,
      recipientType: undefined,
      priceRange: undefined,
    });
    if (companyId) {
      setOptions({});
    }
  }, [companyId, setOptions]);

  const filterFields: FilterField[] = useMemo(() => [
    {
      key: 'code',
      label: 'Buscar por código',
      type: 'text',
      placeholder: 'Buscar por código...',
    },
    {
      key: 'location_id',
      label: 'Ubicación',
      type: 'location',
      locations: locations.map(loc => ({
        id: loc.id,
        code: loc.code,
        name: loc.description,
        description: `${loc.code} - ${loc.description}`,
        warehouse_code: (loc as any).warehouse_code || undefined,
      })),
      specialOption: {
        id: 'sin-ubicar',
        name: 'Sin Ubicar',
        description: 'Contenedores sin ubicación asignada',
      },
    },
    {
      key: 'type',
      label: 'Tipo Contenedor',
      type: 'select',
      options: containerTypes.map((type) => ({
        id: type.id,
        name: type.name || '',
        description: type.name,
      })),
    },
    {
      key: 'category',
      label: 'Categoría',
      type: 'containerCategory',
      containerCategories: containerCategories as any,
    },
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: containerStatuses.map((status) => ({
        id: status.id,
        name: status.name,
        description: status.name,
      })),
    },
    {
      key: 'recipientType',
      label: 'Tipo de Recipiente',
      type: 'select',
      options: containerRecipientTypes.map((recipientType) => ({
        id: recipientType.id,
        name: recipientType.name || '',
        description: recipientType.name,
      })),
    },
    {
      key: 'priceRange',
      label: 'Rango de Precios',
      type: 'select',
      options: [
        { id: 'sin-rango', name: 'Sin rango', description: 'Sin rango' },
        ...priceRanges.map((priceRange) => ({
          id: priceRange.id,
          name: priceRange.description || '',
          description: priceRange.description,
        })),
      ],
    },
  ], [locations, containerTypes, containerCategories, containerStatuses, containerRecipientTypes, priceRanges]);

  return (
    <div className="p-24 sm:p-32 w-full border-b-1 bg-white header-components">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-col items-center sm:items-start">
          <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
            Contenedores
          </Typography>
          <Typography className="text-14 font-medium" color="text.secondary">
            {total} contenedor/es
          </Typography>
        </div>
        <div className="flex w-full sm:w-auto items-center gap-4 mt-4 sm:mt-0">
          <FilterDrawer
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            fields={filterFields}
            disabled={!companyId}
          />

          {canWrite && (
            <Button
              variant="contained"
              color="secondary"
              className="my-auto mx-2"
              disabled={!companyId}
              onClick={openCreateDialog}
              sx={{
                minWidth: 120,
                height: 48,
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 999,
                boxShadow: '0 2px 8px 0 rgba(80,80,200,0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AddCircleOutlineOutlinedIcon sx={{ fontSize: 22 }} />
              <span className="mx-8">Crear</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
